<template>
  <div>
    <v-card>
      <v-card-text>
        <h2><v-icon>lock</v-icon>Security</h2>
        <v-form ref="generateLoginForm" v-if="allowed && !user">
          <v-alert :value="true" type="info"
            >This should be the email address of the group, not of an employee
            (e.g. info@yourmove.co.uk)</v-alert
          >
          <v-text-field label="email" v-model="username" :rules="emailRules" />
          <v-btn @click="generateLogin">Generate login details</v-btn>
        </v-form>
        <!-- <v-switch v-model="allowed" label="Login access" @change="updateDeny" /> -->
        <template v-if="loginDetails && loginDetails.loginUser">
          <template v-if="!loginDetails.deny">
            <table>
              <tr>
                <td>
                  <strong>Username</strong>
                </td>
                <td>
                  <a
                    v-if="loginDetails.primary"
                    :href="`mailto:${loginDetails.loginUser.username}`"
                    >{{ loginDetails.loginUser.username }}</a
                  >
                  <template v-if="!loginDetails.primary">
                    <v-text-field
                      v-model="loginDetails.loginUser.username"
                      @input="usernameDebounce"
                      v-if="$keycloak.hasRealmRole('root')"
                    />
                    <span v-if="!$keycloak.hasRealmRole('root')">{{
                      loginDetails.loginUser.username
                    }}</span>
                  </template>
                </td>
              </tr>
              <tr v-if="$keycloak.hasRealmRole('root')">
                <td>
                  <strong>Password</strong>
                </td>
                <td>
                  {{ loginDetails.loginUser.password }}
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn @click="generatePassword" small v-on="on"
                        >Generate new password</v-btn
                      >
                    </template>
                    <span>Generate a new password</span>
                  </v-tooltip>
                </td>
              </tr>
            </table>
            <!-- <v-switch
              v-model="optin"
              label="Email notifications"
              @change="updateOptout"
              v-if="loginDetails.primary"
            /> -->
          </template>
        </template>
      </v-card-text>
    </v-card>
    <v-card v-if="user">
      <v-card-text>
        <h2>OpenID Connect</h2>
        <v-btn v-if="!userRepresentation" @click="createUserRepresentation"
          >Create user representation</v-btn
        >
        <template v-if="userRepresentation">
          <div v-if="!group.openid">
            Openid not linked to record!
            <v-btn color="error" @click="updateOpenid()">Reset openid</v-btn>
          </div>
          <div v-if="group.openid">
            <v-text-field label="Id" :value="userRepresentation.id" readonly />
            <v-data-table
              :headers="headers"
              :items="federatedIdentityRepresentations"
              v-if="federatedIdentityRepresentations"
              hide-actions
              class="elevation-1"
            >
              <template v-slot:items="props">
                <tr>
                  <td>{{ props.item.identityProvider }}</td>
                  <td>{{ props.item.userId }}</td>
                  <td>{{ props.item.userName }}</td>
                  <td>
                    <a :href="`https://facebook.com/${props.item.userId}`"
                      >facebook</a
                    >
                    <a :href="`https://plus.google.com/${props.item.userId}`"
                      >google</a
                    >
                    <a
                      :href="`https://www.linkedin.com/in/${props.item.userName}`"
                      >linkedin</a
                    >
                  </td>
                </tr>
              </template>
            </v-data-table>
            <v-alert :value="!federatedIdentityRepresentations" type="warning"
              >No federated identity representations</v-alert
            >
            <v-text-field
              label="Verification token status"
              :value="group.verificationTokenStatus"
              v-if="group.verificationTokenStatus"
              readonly
            />
            <v-text-field
              label="Verification token sent"
              :value="group.verificationTokenSent"
              v-if="group.verificationTokenSent"
              readonly
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn @click="executeActionsEmail" color="info" v-on="on">
                  <v-icon>send</v-icon>Verification
                </v-btn>
              </template>
              <span>Send verification email</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn @click="updateRoles" v-on="on">
                  <v-icon>refresh</v-icon>Roles
                </v-btn>
              </template>
              <span>Update user roles</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn @click="resetPassword" v-on="on">Reset password</v-btn>
              </template>
              <span>Set a password on OpenID</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="removeUserRepresentation"
                  color="error"
                  v-on="on"
                >
                  <v-icon>delete</v-icon>Delete
                </v-btn>
              </template>
              <span>Delete user representation</span>
            </v-tooltip>
            <div>Openid: {{ group.openid }}</div>
            <div>
              <span v-if="user">{{
                `User, username: ${user.username}, password: ${user.password}, version: ${user.version}`
              }}</span>
            </div>
            <v-alert :value="!user" type="warning">No user</v-alert>
            <div
              v-for="(value, propertyName) in userRepresentation.attributes"
              :key="propertyName"
            >
              {{ `attribute: ${propertyName} ${value}` }}
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>
    <v-snackbar
      v-model="snackbar"
      :multi-line="true"
      :timeout="3000"
      :top="true"
    >
      {{ snackbarText }}
      <v-btn color="pink" flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import emailValidator from "email-validator";
import nbmlaw from "@/api/nbmlaw";
function initialState() {
  return {
    allowed: null,
    optin: false,
    accessDenied: true,
    loginDetails: null,
    userRepresentation: null,
    federatedIdentityRepresentations: null,
    // gdpr: null,
    user: null,
    username: null,
    headers: [
      {
        text: "Identity provider",
        value: "identityProvider"
      },
      {
        text: "User id",
        value: "userId"
      },
      {
        text: "User name",
        value: "userName"
      },
      {
        text: "Links",
        value: "userId"
      }
    ],
    emailRules: [
      (v) => {
        if (!v) {
          return "Email address is required";
        }
        return emailValidator.validate(v) || "Invalid e-mail.";
      }
    ],
    snackbar: false,
    snackbarText: null,
    timerId: null
  };
}
export default {
  name: "Security",
  props: ["group"],
  data: function () {
    return initialState();
  },
  methods: {
    async fetchLoginDetails() {
      try {
        const response = await nbmlaw.get(
          `/cors/admin/directory/groups/${this.$route.params.id}/loginDetails`
        );
        // console.log(response.data);
        this.loginDetails = response.data;
      } catch (err) {
        Object.assign(this.$data, initialState());
      }
    },
    async fetchUser() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/groups/${this.$route.params.id}/user`
      );
      // console.log(response.data);
      this.user = response.data;
    },
    // async fetchGdpr() {
    //   const response = await nbmlaw.get(
    //     `/cors/admin/directory/groups/${this.$route.params.id}/gdpr`
    //   );
    //   // console.log(response.data);
    //   this.gdpr = response.data;
    // },
    async fetchUserRepresentation() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/groups/${this.$route.params.id}/userRepresentation`
      );
      // console.log(response.data);
      this.userRepresentation = response.data;
    },
    async fetchFederatedIdentityRepresentations() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/groups/${this.$route.params.id}/federatedIdentityRepresentations`
      );
      // console.log(response.data);
      this.federatedIdentityRepresentations = response.data;
    },
    async generatePassword() {
      await nbmlaw.put(
        `/cors/admin/directory/groups/${this.$route.params.id}/password`,
        null
      );
      // console.log(response.data);
      this.update();
      this.snackbar = true;
      this.snackbarText = "New password has been generated";
    },
    // async updateDeny() {
    //   await nbmlaw.put(
    //     `/cors/admin/directory/groups/${this.$route.params.id}/deny/${!this
    //       .allowed}`,
    //     null
    //   );
    //   // console.log(response.data);
    //   this.update();
    //   this.snackbar = true;
    //   this.snackbarText = "Access has been changed";
    // },
    async createUserRepresentation() {
      try {
        await nbmlaw.post(
          `/cors/admin/directory/groups/${this.$route.params.id}/userRepresentation`,
          null
        );
        // console.log(response.data);
        this.$emit("triggerUpdate");
      } catch (err) {
        this.snackbar = true;
        this.snackbarText = err.response.data;
      }
    },
    async removeUserRepresentation() {
      if (confirm("Are you sure?")) {
        await this.deleteUserRepresentation();
        this.snackbar = true;
        this.snackbarText = "Deleted OpenID account";
      }
    },
    async deleteUserRepresentation() {
      try {
        await nbmlaw.delete(
          `/cors/admin/directory/groups/${this.$route.params.id}/userRepresentation`
        );
        this.$emit("triggerUpdate");
      } catch (err) {
        this.snackbar = true;
        this.snackbarText = err;
      }
    },
    async executeActionsEmail() {
      try {
        await nbmlaw.put(
          `/cors/admin/directory/groups/${this.$route.params.id}/executeActionsEmail`,
          null
        );
        // console.log(response.data);
        this.$emit("triggerUpdate");
        this.snackbar = true;
        this.snackbarText = "Sent email";
      } catch (err) {
        this.snackbar = true;
        this.snackbarText = err;
      }
    },
    async updateRoles() {
      await nbmlaw.put(
        `/cors/admin/directory/groups/${this.$route.params.id}/userRoles/keycloak`,
        null
      );
      // console.log(response.data);
      this.update();
      this.snackbar = true;
      this.snackbarText = "Updated user roles for Open id";
    },
    async resetPassword() {
      const response = await nbmlaw.put(
        `/cors/admin/directory/groups/${this.$route.params.id}/password/keycloak`,
        null
      );
      // console.log(response.data);
      this.update();
      this.snackbar = true;
      this.snackbarText = "Reset password, result: " + response.data;
    },
    async updateOpenid() {
      try {
        await nbmlaw.put(
          `/cors/admin/directory/groups/${this.$route.params.id}/openid`,
          null
        );
        // console.log(response.data);
        this.$emit("triggerUpdate");
      } catch (err) {
        this.snackbar = true;
        this.snackbarText = err;
      }
    },
    async generateLogin() {
      if (this.$refs.generateLoginForm.validate()) {
        try {
          await nbmlaw.put(
            `/cors/admin/directory/groups/${this.$route.params.id}/username`,
            null,
            { params: { username: this.username } }
          );
          // console.log(response.data);
          this.$emit("triggerUpdate");
        } catch (err) {
          this.snackbar = true;
          this.snackbarText = "Generate login, result: " + err;
        }
      }
    },
    async updateUsername() {
      await nbmlaw.put(
        `/cors/admin/directory/groups/${this.$route.params.id}/username`,
        null,
        { params: { username: this.loginDetails.loginUser.username } }
      );
      // console.log(response.data);
      this.$emit("triggerUpdate");
    },
    usernameDebounce() {
      clearTimeout(this.timerId);
      this.timerId = setTimeout(() => {
        this.updateUsername();
      }, 500);
    },
    update() {
      this.fetchLoginDetails();
      this.fetchUser();
      //   this.fetchGdpr();
      this.fetchUserRepresentation();
      this.fetchFederatedIdentityRepresentations();
    }
  },
  watch: {
    group: {
      immediate: true,
      handler() {
        this.update();
      }
    },
    loginDetails: {
      handler() {
        this.allowed = this.loginDetails ? !this.loginDetails.deny : null;
        this.optin = this.loginDetails ? !this.loginDetails.optout : false;
      }
    }
  }
};
</script>
